import React from 'react';
import {
  Checkbox as MuiCheckbox,
  CheckboxProps as MuiCheckboxProps,
  Typography,
  SvgIcon,
} from '@mui/material';
import { PounceStyleProps, extractPounceProps, mapPouncePropsToSx } from '../../utils';
import { Box } from '../Box';

// Unchecked icon - empty box with 1px border
const UncheckedIcon = () => (
  <SvgIcon viewBox="0 0 24 24">
    <rect
      x="0.5"
      y="0.5"
      width="23"
      height="23"
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
      rx="4"
    />
  </SvgIcon>
);

// Checked icon - box with checkmark, no background fill (matching original pounce)
const CheckedIcon = () => (
  <SvgIcon viewBox="0 0 24 24">
    <rect
      x="0.5"
      y="0.5"
      width="23"
      height="23"
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
      rx="4"
    />
    {/* Centered checkmark using original pounce path */}
    <g transform="translate(3, 2)">
      <svg width="18" height="18" viewBox="0 0 20 18" fill="white">
        <path d="M7 14.17L2.83 10l-1.41 1.41L7 17 19 5l-1.41-1.42L7 14.17z" fill="currentColor" />
      </svg>
    </g>
  </SvgIcon>
);

// Indeterminate icon - box with dash, no background fill (matching original pounce)
const IndeterminateIcon = () => (
  <SvgIcon viewBox="0 0 24 24">
    <rect
      x="0.5"
      y="0.5"
      width="23"
      height="23"
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
      rx="4"
    />
    {/* Centered dash using original pounce path */}
    <g transform="translate(0, 0)">
      <svg width="24" height="18" viewBox="0 0 24 18" fill="white">
        <path d="M20 13.8571V13.1429H4V10.8571H10.1429V10.8571H20V13.1429Z" fill="currentColor" />
      </svg>
    </g>
  </SvgIcon>
);

export interface CheckboxProps
  extends Omit<MuiCheckboxProps, 'size' | keyof PounceStyleProps>, PounceStyleProps {
  /** The label associated with the Checkbox. Appears on the right. */
  label?: string;

  /** Whether the checkbox is currently disabled */
  disabled?: boolean;

  /**  Whether the input has an invalid value or not */
  invalid?: boolean;

  /**  Whether the input state cannot be determined in binary terms */
  indeterminate?: boolean;

  size?: 'small' | 'large';

  /** Optional styles to add to the label next to the checkbox */
  labelSx?: PounceStyleProps;

  /** Additional styles to apply (combined with pounce props) */
  sx?: MuiCheckboxProps['sx'];
}

/**
 *  Your bread & butter checkbox element. Nothing new here
 *  */
const CheckboxComponent = React.forwardRef<HTMLButtonElement, CheckboxProps>(function Checkbox(
  {
    checked,
    label,
    disabled,
    readOnly,
    invalid,
    indeterminate,
    hidden,
    labelSx,
    size = 'large',
    sx,
    ...rest
  },
  ref,
) {
  const { pounceProps, otherProps } = extractPounceProps(rest);
  const pounceStyles = mapPouncePropsToSx(pounceProps);

  if (!label && !(otherProps['aria-label'] || otherProps['aria-labelledby'])) {
    // eslint-disable-next-line no-console
    console.error(
      'The `label` prop was omitted without providing an `aria-label` or `aria-labelledby` attribute',
    );
  }

  // Combine pounce-generated styles with user sx
  const combinedSx = {
    ...pounceStyles,
    ...(sx || {}),
  } as MuiCheckboxProps['sx'];

  // Create label styles if labelSx is provided
  const labelStyles = labelSx ? mapPouncePropsToSx(labelSx) : {};

  const checkboxElement = (
    <MuiCheckbox
      ref={ref}
      checked={checked}
      disabled={disabled}
      readOnly={readOnly}
      indeterminate={indeterminate}
      color={invalid ? 'error' : 'primary'}
      size={size === 'small' ? 'small' : 'medium'}
      icon={<UncheckedIcon />}
      checkedIcon={<CheckedIcon />}
      indeterminateIcon={<IndeterminateIcon />}
      sx={combinedSx}
      slotProps={{
        input: {
          'aria-invalid': invalid,
          'aria-readonly': readOnly,
        },
      }}
      {...otherProps}
    />
  );

  return (
    <Box
      as="label"
      display="inline-flex"
      alignItems="center"
      cursor={disabled ? 'default' : 'pointer'}
      fontSize="medium"
      fontWeight="medium"
      verticalAlign="top"
      aria-disabled={disabled}
      hidden={hidden}
      aria-hidden={hidden}
    >
      {checkboxElement}
      {label && (
        <Typography
          component="span"
          fontSize={size === 'small' ? 'small' : 'medium'}
          fontWeight="medium"
          sx={{
            userSelect: 'none',
            ...labelStyles,
          }}
        >
          {label}
        </Typography>
      )}
    </Box>
  );
});

export const Checkbox = React.memo(CheckboxComponent);
